import { useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  Button,
  Center,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';

import {
  CountryCode,
  DropdownOption,
  FilterData,
  Filters,
  formatAmountRange,
  getAmountStringsFromParams,
  MultiSelectionData,
  SearchNormalOutline,
  TransactionDirection,
  useToast,
  DocumentDownloadOutline,
} from '@aslanpay/portal-common-lib';

import CreateTransactionNoteModal from 'app/components/Transactions/CreateTransactionNote/CreateTransactionNoteModal';
import ViewTransactionNotesModal from 'app/components/Transactions/ViewTransactionNotes/ViewTransactionNotesModal';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import { Table } from 'app/components/UI/Table/Table';
import {
  SuspiciousTransactionActions,
  SuspiciousTransactionBulkActions,
} from 'app/components/UI/Table/Table.types';

import { countries } from 'app/constants/countries';
import { defaultPageSize } from 'app/constants/table';
import { TransactionDirectionLabels } from 'app/constants/transactions';

import useDebounce from 'app/hooks/useDebounce';
import useExportTransactions from 'app/hooks/useExportTransactions';
import useFetchTransactionsPage from 'app/hooks/useFetchTransactionsPage';

import getSuspiciousTransactionColumns from 'app/utils/table/suspiciousTransactions';

import type { Filter } from '@aslanpay/portal-common-lib';

const amountOptions: DropdownOption[] = [
  { value: '1000-', text: 'Up to 1,000' },
  { value: '1000-5000', text: 'Between 1,000 - 5,000' },
  { value: '5000-10000', text: 'Between 5,000 - 10,000' },
  { value: '10000+', text: 'More than 10,000' },
];

const transactionDirectionOptions: DropdownOption[] = Object.entries(
  TransactionDirectionLabels
).map(([value, text]) => ({
  value,
  text,
}));

const countriesOptions: DropdownOption[] = countries.map((country) => ({
  value: country.code,
  text: country.details.name,
}));

function SuspiciousActivity() {
  const toast = useToast();

  const queryClient = useQueryClient();

  const { isOpen: isAddNoteOpen, onOpen: onOpenAddNote, onClose: onCloseAddNote } = useDisclosure();
  const {
    isOpen: isViewNotesOpen,
    onOpen: onOpenViewNotes,
    onClose: onCloseViewNotes,
  } = useDisclosure();

  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('query') || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [transactionsActionIds, setTransactionsActionIds] = useState<string[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);
  const [directionsFilter, setDirectionsFilter] = useState<TransactionDirection[]>(
    searchParams.get('directions')
      ? (searchParams.get('directions')?.split(',') as TransactionDirection[])
      : []
  );
  const [countriesFilter, setCountriesFilter] = useState<CountryCode[]>(
    searchParams.get('countries')
      ? (searchParams.get('countries')?.split(',') as CountryCode[])
      : []
  );
  const [minAmountFilter, setMinAmountFilter] = useState<number | undefined>(
    searchParams.get('min') ? Number(searchParams.get('min')) : undefined
  );
  const [maxAmountFilter, setMaxAmountFilter] = useState<number | undefined>(
    searchParams.get('max') ? Number(searchParams.get('max')) : undefined
  );
  const [amountStringsFilter, setAmountStringsFilter] = useState<string[]>(
    getAmountStringsFromParams(
      searchParams.get('min') ? Number(searchParams.get('min')) : undefined,
      searchParams.get('max') ? Number(searchParams.get('max')) : undefined,
      amountOptions
    )
  );
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  const resetPagination = () => {
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const handleDirectionsChange = (values: (string | number)[]) => {
    setDirectionsFilter(values as TransactionDirection[]);
    resetPagination();
  };

  const handleCountriesChange = (values: (string | number)[]) => {
    setCountriesFilter(values as CountryCode[]);
    resetPagination();
  };

  const handleAmountChange = (minAmount: number | undefined, maxAmount: number | undefined) => {
    setMinAmountFilter(minAmount);
    setMaxAmountFilter(maxAmount);
    resetPagination();
  };

  const handleFilterChange = (filterName: string, newValues: string[]) => {
    switch (filterName) {
      case 'Transaction direction':
        handleDirectionsChange(newValues);
        break;
      case 'Country':
        handleCountriesChange(newValues);
        break;
      case 'Amount': {
        const { minAmount, maxAmount } = formatAmountRange(newValues);

        handleAmountChange(minAmount, maxAmount);

        const aggregatedStrings = getAmountStringsFromParams(minAmount, maxAmount, amountOptions);
        setAmountStringsFilter(aggregatedStrings);
        break;
      }
      default:
        break;
    }
  };

  const { data: transactions, isLoading: isLoadingTransactions } = useFetchTransactionsPage({
    directions: directionsFilter,
    countryCodes: countriesFilter,
    minAmount: minAmountFilter,
    maxAmount: maxAmountFilter,
    isSuspicious: true,
    query: debouncedSearchQuery,
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const filters: Filter[] = [
    {
      name: 'Transaction direction',
      type: 'category',
      data: transactionDirectionOptions,
      selectedValues: directionsFilter,
    },
    {
      name: 'Country',
      type: 'category',
      data: countriesOptions,
      selectedValues: countriesFilter,
    },
    {
      name: 'Amount',
      type: 'range',
      data: amountOptions,
      selectedValues: amountStringsFilter,
    },
  ];

  const { mutate: exportTransactions } = useExportTransactions({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Suspicious transactions report downloaded',
      });
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to download the file. Please try again.',
      });
    },
  });

  const handleAddNote = (ids: number[]) => {
    const transactionsIds = ids.map((id) => transactions?.list[id]?.id || '');

    setTransactionsActionIds(transactionsIds);
    onOpenAddNote();
  };

  const handleViewNotes = (ids: number[]) => {
    const transactionsIds = ids.map((id) => transactions?.list[id]?.id || '');

    setTransactionsActionIds(transactionsIds);
    onOpenViewNotes();
  };

  const handleExportTransactions = (ids: number[]) => {
    const transactionsIds = ids.map((id) => transactions?.list[id]?.id || '');

    exportTransactions({ ids: transactionsIds });

    setRowSelection({});
  };

  const handleInvalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchTransactionsPage'] });

    setTransactionsActionIds([]);
    setRowSelection({});
  };

  const handleFilterClick = (filterName: string) => {
    setActiveFilter(filterName);
  };

  const debouncedTyping = useDebounce((value: string) => {
    setDebouncedSearchQuery(value);
    resetPagination();
  }, 500);

  const onSearchInput = (value: string) => {
    setSearchQuery(value);
    debouncedTyping(value);
  };

  const formatFilterValue = (
    filterName: string,
    filterValue: string | number,
    filterData: FilterData
  ) => {
    switch (filterName) {
      case 'Amount':
      case 'Country':
      case 'Transaction direction':
        return (
          (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
          String(filterValue)
        );
      default:
        return String(filterValue);
    }
  };

  const actions: SuspiciousTransactionActions = {
    addNote: handleAddNote,
    viewNotes: handleViewNotes,
    export: handleExportTransactions,
  };

  const bulkActions: SuspiciousTransactionBulkActions = {
    addNote: { action: handleAddNote },
    export: { action: handleExportTransactions },
  };

  useEffect(() => {
    setSearchParams(
      {
        page: `${pageIndex + 1}`,
        ...(searchQuery && { query: searchQuery }),
        ...(sorting[0] && { sortBy: sorting[0]?.id, sort: sorting[0]?.desc ? 'DESC' : 'ASC' }),
        ...(directionsFilter.length && { directions: directionsFilter.join(',') }),
        ...(countriesFilter.length && { countries: countriesFilter.join(',') }),
        ...(minAmountFilter && { min: minAmountFilter.toString() }),
        ...(maxAmountFilter && { max: maxAmountFilter.toString() }),
      },
      { replace: true }
    );
  }, [
    pageIndex,
    searchQuery,
    sorting,
    directionsFilter,
    countriesFilter,
    minAmountFilter,
    maxAmountFilter,
    setSearchParams,
  ]);

  return (
    <AuthenticatedPageWrapper title="Suspicious activity">
      <CreateTransactionNoteModal
        selectedIds={transactionsActionIds}
        isOpen={isAddNoteOpen}
        onClose={onCloseAddNote}
        onCreate={handleInvalidateQueries}
      />
      <ViewTransactionNotesModal
        selectedIds={transactionsActionIds}
        isOpen={isViewNotesOpen}
        onClose={onCloseViewNotes}
      />
      <Text as="h2" textStyle="h2" color="brand.darkPurple">
        Suspicious activity
      </Text>

      <Flex justify="space-between" align="center" mt="10px" gap="30px">
        <Text as="h5" textStyle="h5SemiBold" color="brand.darkGray">
          Transactions list
        </Text>
        <Spacer />
        <InputGroup
          maxW="278px"
          aria-disabled={!searchQuery.length && !transactions?.list.length}
          color="brand.darkGray"
          _disabled={{ color: 'system.disabled' }}
        >
          <Input
            isDisabled={!searchQuery.length && !transactions?.list.length}
            onChange={(event) => onSearchInput(event.target.value)}
            defaultValue={searchQuery}
            type="text"
            placeholder="Search"
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>
        <Button
          leftIcon={<DocumentDownloadOutline boxSize="24px" />}
          size="md"
          onClick={() => exportTransactions({ isSuspicious: true })}
        >
          Download suspicious transactions report
        </Button>
      </Flex>
      <Filters
        activeFilter={activeFilter}
        displayFormatter={formatFilterValue}
        filters={filters}
        onFilterClose={() => setActiveFilter(undefined)}
        onFilterChange={handleFilterChange}
        onFilterClick={handleFilterClick}
      />
      {isLoadingTransactions ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <Table
          columns={getSuspiciousTransactionColumns(actions)}
          data={transactions?.list || []}
          total={transactions?.total || 0}
          pageNumber={transactions?.pageNumber || pageIndex}
          pageSize={transactions?.pageSize || pageSize}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          getRowId={(transaction) => transaction.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={bulkActions}
          bulkActionsType="suspiciousTransaction"
          showHover={false}
        />
      )}
    </AuthenticatedPageWrapper>
  );
}

export default SuspiciousActivity;
